/**
 * JobsListPage — Top-level Jobs tab (v1.15).
 *
 * Two responsibilities:
 *   1. Browse / filter all jobs in the system.
 *   2. Bulk-import jobs from a CSV file (the headline new capability for
 *      v1.15). The importer follows the same pattern as the supplier price
 *      book import: dialog → file picker / drag-drop → preview table with
 *      validity flags → confirm.
 *
 * Read-only matching against existing jobs prevents duplicates by jobRef
 * (case-insensitive). The actual de-dup happens server-side in
 * DataContext.addJobs().
 */
import React, { useState, useRef, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Stack, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField,
  InputAdornment, MenuItem, Select, FormControl, InputLabel, Alert, Tooltip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import WorkIcon from '@mui/icons-material/Work';
import EventIcon from '@mui/icons-material/Event';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useData } from '../context/DataContext';
import { Job, JobStatus } from '../data/types';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../theme/tokens';

const STATUS_COLORS: Record<JobStatus, { color: string; bg: string; border: string }> = {
  'Scheduled':   { color: '#0277bd', bg: '#e3f2fd', border: '#90caf9' },
  'In Progress': { color: '#ef6c00', bg: '#fff3e0', border: '#ffb74d' },
  'Completed':   { color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
  'On Hold':     { color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8' },
  'Cancelled':   { color: '#757575', bg: '#eeeeee', border: '#bdbdbd' },
};

// Header tokens we accept in the import CSV. All lowercase / trim'd before lookup.
// The user can omit any optional column — the importer defaults sensibly.
const HEADER_ALIASES: Record<string, string> = {
  // required
  'job ref':        'jobRef',
  'jobref':         'jobRef',
  'reference':      'jobRef',
  'job number':     'number',
  'number':         'number',
  // identification
  'title':          'title',
  'job title':      'title',
  'customer':       'customer',
  'customer name':  'customer',
  'address':        'address',
  // classification
  'workflow':       'workflow',
  'job type':       'jobTypes',
  'job types':      'jobTypes',
  // scheduling
  'technician':     'technician',
  'tech':           'technician',
  'visit date':     'visitDate',
  'date':           'visitDate',
  'start time':     'startTime',
  'end time':       'endTime',
  // money / status
  'amount':         'jobAmount',
  'job amount':     'jobAmount',
  'hours':          'timesheetHours',
  'timesheet hours':'timesheetHours',
  'status':         'status',
  // contact
  'phone':          'phone',
  'email':          'email',
  // free-form
  'notes':          'notes',
};

type ParsedRow = {
  raw: Record<string, string>;
  job: Job;
  valid: boolean;
  warnings: string[];
  duplicate: boolean;
};

const fmt = (n: number) =>
  '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const JobsListPage: React.FC = () => {
  const { jobs, addJobs } = useData();
  const navigate = useNavigate();

  // ── List filters ────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | JobStatus>('all');

  const filteredJobs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs.filter(j => {
      if (statusFilter !== 'all' && j.status !== statusFilter) return false;
      if (!q) return true;
      return (
        j.jobRef.toLowerCase().includes(q) ||
        j.number.toLowerCase().includes(q) ||
        j.title.toLowerCase().includes(q) ||
        j.customer.toLowerCase().includes(q) ||
        j.technician.toLowerCase().includes(q) ||
        (j.workflow || '').toLowerCase().includes(q)
      );
    });
  }, [jobs, search, statusFilter]);

  // ── Import dialog ───────────────────────────────────────────────────────
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<ParsedRow[]>([]);
  const [importFilename, setImportFilename] = useState('');
  const [importSummary, setImportSummary] = useState<{ added: number; skipped: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const resetImport = () => {
    setImportRows([]);
    setImportFilename('');
    setImportSummary(null);
  };

  const closeImport = () => {
    setImportOpen(false);
    resetImport();
  };

  /**
   * Minimal CSV row splitter that honours quoted fields and embedded commas.
   * Not a full RFC-4180 parser, but covers everything our suppliers actually
   * send (single-line fields, optional double-quotes).
   */
  const splitCsvLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        out.push(cur); cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map(s => s.trim());
  };

  const parseFile = (file: File) => {
    setImportFilename(file.name);
    setImportSummary(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = (evt.target?.result as string) ?? '';
      const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
      if (lines.length < 2) {
        setImportRows([]);
        return;
      }
      // Map header → canonical field name
      const headerCols = splitCsvLine(lines[0]).map(h => h.trim().toLowerCase());
      const fieldByIdx: (string | null)[] = headerCols.map(h => HEADER_ALIASES[h] ?? null);

      const seenRefs = new Set(jobs.map(j => j.jobRef.trim().toLowerCase()));
      const seenInCsv = new Set<string>();

      const rows: ParsedRow[] = lines.slice(1).map((line, idx) => {
        const cols = splitCsvLine(line);
        const raw: Record<string, string> = {};
        const fields: Record<string, string> = {};
        cols.forEach((val, i) => {
          const canonical = fieldByIdx[i];
          if (canonical) fields[canonical] = val;
          if (headerCols[i]) raw[headerCols[i]] = val;
        });

        const warnings: string[] = [];
        const jobRef = (fields.jobRef || '').trim();
        if (!jobRef) warnings.push('Missing job ref');

        const refKey = jobRef.toLowerCase();
        const dupAgainstExisting = !!jobRef && seenRefs.has(refKey);
        const dupWithinCsv       = !!jobRef && seenInCsv.has(refKey);
        if (dupAgainstExisting) warnings.push('Already exists in system');
        if (dupWithinCsv)       warnings.push('Duplicated in this CSV');
        if (refKey) seenInCsv.add(refKey);

        // Status normalization
        let status: JobStatus = 'Scheduled';
        const rawStatus = (fields.status || '').trim().toLowerCase();
        if (rawStatus.startsWith('in')) status = 'In Progress';
        else if (rawStatus.startsWith('comp')) status = 'Completed';
        else if (rawStatus.startsWith('on') || rawStatus === 'hold') status = 'On Hold';
        else if (rawStatus.startsWith('canc')) status = 'Cancelled';
        else if (rawStatus.startsWith('sch') || !rawStatus) status = 'Scheduled';
        else { status = 'Scheduled'; warnings.push(`Unknown status "${fields.status}" → Scheduled`); }

        // Number / amount / hours
        const amount = parseFloat(fields.jobAmount || '0') || 0;
        const hours  = parseFloat(fields.timesheetHours || '0') || 0;
        const number = (fields.number || jobRef.replace(/[^0-9]/g, '') || String(idx + 1));

        const job: Job = {
          id: jobRef || `JOB-IMPORT-${Date.now()}-${idx}`,
          jobRef,
          number,
          title: fields.title || `${fields.customer || 'Untitled'}${fields.workflow ? ' · ' + fields.workflow : ''}`,
          customer: fields.customer || '',
          address: fields.address || '',
          workflow: fields.workflow || '',
          jobTypes: fields.jobTypes || fields.workflow || '',
          technician: fields.technician || '',
          visitDate: fields.visitDate || '',
          startTime: fields.startTime || '',
          endTime: fields.endTime || '',
          jobAmount: amount,
          timesheetHours: hours,
          status,
          phone: fields.phone || undefined,
          email: fields.email || undefined,
          notes: fields.notes || undefined,
          createdAt: new Date().toISOString(),
          importedFrom: file.name,
        };

        return {
          raw, job,
          valid: !!jobRef && !dupAgainstExisting && !dupWithinCsv,
          warnings,
          duplicate: dupAgainstExisting || dupWithinCsv,
        };
      });
      setImportRows(rows);
    };
    reader.readAsText(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) parseFile(file);
  };

  const downloadTemplate = () => {
    const csv =
      'Job Ref,Job Number,Title,Customer,Address,Workflow,Job Type,Technician,Visit Date,Start Time,End Time,Amount,Hours,Status,Phone,Email,Notes\n' +
      'JOB-301,301,Crown Casino · HVAC Service,Crown Casino,8 Whiteman St Southbank VIC,HVAC Service,Annual maintenance,Jake Morrison,12/06/2026,9 AM,1 PM,1850,4,Scheduled,03 9292 8888,facilities@crown.com.au,Annual service\n' +
      'JOB-302,302,Westfield Carousel · Plumbing,Westfield Carousel,1382 Albany Hwy Cannington WA,Plumbing Repair,Emergency callout,Brooke Nguyen,15/06/2026,8 AM,10 AM,650,2,Scheduled,08 9311 2828,operations@scentregroup.com,Leaking riser on level 2\n' +
      'JOB-303,303,Royal Perth Hospital · Electrical Test,Royal Perth Hospital,197 Wellington St Perth WA,Electrical Inspection,Compliance audit,Chris Patel,18/06/2026,7 AM,12 PM,1240,5,In Progress,08 9224 2244,maintenance@health.wa.gov.au,RCD testing site-wide\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'jobs-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleConfirmImport = () => {
    const valid = importRows.filter(r => r.valid).map(r => r.job);
    if (valid.length === 0) return;
    const summary = addJobs(valid);
    setImportSummary(summary);
    setImportRows([]);
  };

  const validCount   = importRows.filter(r => r.valid).length;
  const invalidCount = importRows.length - validCount;

  // ── Stats for the page header ───────────────────────────────────────────
  const stats = useMemo(() => {
    const byStatus: Record<JobStatus, number> = {
      'Scheduled': 0, 'In Progress': 0, 'Completed': 0, 'On Hold': 0, 'Cancelled': 0,
    };
    for (const j of jobs) byStatus[j.status]++;
    const totalValue = jobs.reduce((s, j) => s + j.jobAmount, 0);
    return { byStatus, totalValue };
  }, [jobs]);

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" mb={2} spacing={2}>
        <Box sx={{ width: 38, height: 38, borderRadius: '50%', backgroundColor: '#e3f2fd',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <WorkIcon sx={{ color: BRAND }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#333' }}>
            Jobs
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#888' }}>
            Browse all scheduled, in-progress and completed jobs. Bulk-import from a CSV.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
          onClick={downloadTemplate}
          sx={{ textTransform: 'none', borderColor: '#ccc', color: '#555',
            '&:hover': { borderColor: BRAND, color: BRAND } }}>
          Download template
        </Button>
        <Button variant="contained" size="small" startIcon={<UploadFileIcon />}
          onClick={() => setImportOpen(true)}
          sx={{ textTransform: 'none', backgroundColor: BRAND, fontWeight: 700,
            '&:hover': { backgroundColor: '#4a3580' } }}>
          Import Jobs
        </Button>
      </Stack>

      {/* ── Stats band ───────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap">
        {(['Scheduled', 'In Progress', 'Completed', 'On Hold', 'Cancelled'] as JobStatus[]).map(s => {
          const cfg = STATUS_COLORS[s];
          const count = stats.byStatus[s];
          const active = statusFilter === s;
          return (
            <Chip key={s}
              label={`${count} ${s}`}
              size="small"
              variant={active ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter(active ? 'all' : s)}
              sx={{
                backgroundColor: active ? cfg.bg : 'transparent',
                color: cfg.color, borderColor: cfg.border,
                fontWeight: 700, fontSize: '0.7rem', cursor: 'pointer',
                '&:hover': { backgroundColor: cfg.bg },
              }} />
          );
        })}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>
          Pipeline value: <strong style={{ color: BRAND }}>{fmt(stats.totalValue)}</strong>
        </Typography>
      </Stack>

      {/* ── Search bar ───────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1.5} mb={2} alignItems="center">
        <TextField
          size="small"
          placeholder="Search by ref, title, customer, technician…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 480, '& .MuiInputBase-input': { fontSize: '0.82rem' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#888' }} /></InputAdornment>
            ),
          }}
        />
        {statusFilter !== 'all' && (
          <Chip
            label={`Status: ${statusFilter}`}
            size="small"
            onDelete={() => setStatusFilter('all')}
            sx={{ fontSize: '0.7rem', backgroundColor: '#f5f0ff', color: BRAND, fontWeight: 700 }} />
        )}
      </Stack>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <Paper sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& .MuiTableCell-root': { backgroundColor: '#f9f7ff', py: 0.8,
              fontWeight: 700, fontSize: '0.65rem', color: '#666',
              textTransform: 'uppercase', letterSpacing: 0.4 } }}>
              <TableCell sx={{ width: 96 }}>Status</TableCell>
              <TableCell sx={{ width: 100 }}>Job Ref</TableCell>
              <TableCell sx={{ width: 60 }}>No.</TableCell>
              <TableCell>Title</TableCell>
              <TableCell sx={{ width: 160 }}>Customer</TableCell>
              <TableCell sx={{ width: 110 }}>Visit date</TableCell>
              <TableCell sx={{ width: 130 }}>Technician</TableCell>
              <TableCell align="right" sx={{ width: 90 }}>Amount</TableCell>
              <TableCell sx={{ width: 44 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredJobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 6, textAlign: 'center', color: '#aaa', fontSize: '0.82rem' }}>
                  {jobs.length === 0
                    ? 'No jobs yet — click "Import Jobs" to bulk-import from a CSV, or "Download template" to see the format.'
                    : 'No jobs match the current filter.'}
                </TableCell>
              </TableRow>
            ) : filteredJobs.map(j => {
              const cfg = STATUS_COLORS[j.status];
              return (
                <TableRow key={j.id} hover sx={{ cursor: 'pointer' }}
                  onClick={() => navigate('/schedule')}>
                  <TableCell>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4,
                      px: 0.8, py: 0.2, borderRadius: 1,
                      backgroundColor: cfg.bg, border: `1px solid ${cfg.border}` }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.color }} />
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: cfg.color, whiteSpace: 'nowrap' }}>
                        {j.status}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 700, color: BRAND }}>
                    {j.jobRef}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#666' }}>
                    {j.number}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#333' }}>
                    {j.title}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.72rem', color: '#666' }}>
                    {j.customer || <span style={{ color: '#ccc' }}>—</span>}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.72rem', color: '#666' }}>
                    <Stack direction="row" spacing={0.4} alignItems="center">
                      <EventIcon sx={{ fontSize: 12, color: '#bbb' }} />
                      <span>{j.visitDate || '—'}</span>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.72rem', color: '#666' }}>
                    {j.technician || <span style={{ color: '#ccc' }}>—</span>}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#333' }}>
                    {j.jobAmount > 0 ? fmt(j.jobAmount) : <span style={{ color: '#ccc' }}>—</span>}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Open in Schedule">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate('/schedule'); }}>
                        <OpenInNewIcon sx={{ fontSize: 14, color: '#aaa' }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* ── Import dialog ────────────────────────────────────────────────── */}
      <Dialog open={importOpen} onClose={closeImport} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#f5f0ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UploadFileIcon sx={{ color: BRAND }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={800}>Import Jobs from CSV</Typography>
              <Typography variant="caption" color="text.secondary">
                Bulk-create jobs from a CSV file. Duplicates by Job Ref are skipped automatically.
              </Typography>
            </Box>
            <IconButton size="small" onClick={closeImport}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {/* Success banner after a confirmed import */}
          {importSummary && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
              <strong>{importSummary.added} job{importSummary.added !== 1 ? 's' : ''} imported.</strong>
              {importSummary.skipped > 0 && ` ${importSummary.skipped} row${importSummary.skipped !== 1 ? 's' : ''} skipped (duplicates or invalid).`}
            </Alert>
          )}

          {/* Drop zone / file picker */}
          {importRows.length === 0 && !importSummary && (
            <Box
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              sx={{
                border: `2px dashed ${isDragging ? BRAND : '#ccc'}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: isDragging ? '#f5f0ff' : '#fafafa',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: BRAND, backgroundColor: '#f5f0ff' },
              }}>
              <UploadFileIcon sx={{ fontSize: 40, color: isDragging ? BRAND : '#aaa', mb: 1 }} />
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#444' }}>
                Drop your CSV here, or click to browse
              </Typography>
              <Typography sx={{ fontSize: '0.74rem', color: '#888', mt: 0.5 }}>
                Required columns: <strong>Job Ref</strong>. Everything else is optional.&nbsp;
                <Box component="span" onClick={(e) => { e.stopPropagation(); downloadTemplate(); }}
                  sx={{ color: BRAND, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
                  Download template
                </Box>
              </Typography>
              <input ref={fileRef} type="file" accept=".csv" onChange={handleFileInput}
                style={{ position: 'absolute', left: -9999, opacity: 0 }} />
            </Box>
          )}

          {/* Preview table */}
          {importRows.length > 0 && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Chip label={importFilename} size="small"
                  sx={{ backgroundColor: '#f5f0ff', color: BRAND, fontWeight: 700, fontSize: '0.7rem' }} />
                <Chip label={`${validCount} ready to import`} size="small"
                  sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.7rem' }} />
                {invalidCount > 0 && (
                  <Chip label={`${invalidCount} skipped`} size="small"
                    sx={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 700, fontSize: '0.7rem' }} />
                )}
                <Box sx={{ flex: 1 }} />
                <Button size="small" onClick={resetImport} sx={{ textTransform: 'none', color: '#888' }}>
                  Pick a different file
                </Button>
              </Stack>
              <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& .MuiTableCell-root': { backgroundColor: '#f9f7ff', py: 0.6,
                      fontWeight: 700, fontSize: '0.62rem', color: '#666',
                      textTransform: 'uppercase', letterSpacing: 0.4 } }}>
                      <TableCell sx={{ width: 80 }}>Status</TableCell>
                      <TableCell sx={{ width: 100 }}>Job Ref</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell sx={{ width: 140 }}>Customer</TableCell>
                      <TableCell sx={{ width: 110 }}>Visit date</TableCell>
                      <TableCell align="right" sx={{ width: 90 }}>Amount</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importRows.map((r, i) => (
                      <TableRow key={i} sx={{ '& .MuiTableCell-root': { py: 0.5 },
                        backgroundColor: r.valid ? undefined : '#fff8f0' }}>
                        <TableCell>
                          {r.valid ? (
                            <Chip label="Ready" size="small"
                              sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32',
                                fontWeight: 700, fontSize: '0.6rem', height: 18 }} />
                          ) : (
                            <Chip label="Skip" size="small"
                              sx={{ backgroundColor: '#fff3e0', color: '#e65100',
                                fontWeight: 700, fontSize: '0.6rem', height: 18 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.68rem',
                          color: r.duplicate ? '#e65100' : BRAND, fontWeight: 700 }}>
                          {r.job.jobRef || <span style={{ color: '#bbb' }}>(missing)</span>}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', color: '#333' }}>{r.job.title}</TableCell>
                        <TableCell sx={{ fontSize: '0.66rem', color: '#666' }}>
                          {r.job.customer || <span style={{ color: '#bbb' }}>—</span>}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.66rem', color: '#666' }}>
                          {r.job.visitDate || <span style={{ color: '#bbb' }}>—</span>}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>
                          {r.job.jobAmount > 0 ? fmt(r.job.jobAmount) : <span style={{ color: '#bbb' }}>—</span>}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.6rem', color: r.warnings.length ? '#e65100' : '#888' }}>
                          {r.warnings.length > 0 ? r.warnings.join(' · ') : 'OK'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeImport} sx={{ textTransform: 'none', color: '#666' }}>
            {importSummary ? 'Close' : 'Cancel'}
          </Button>
          {importRows.length > 0 && (
            <Button variant="contained" onClick={handleConfirmImport}
              disabled={validCount === 0}
              startIcon={<CheckCircleIcon />}
              sx={{ textTransform: 'none', backgroundColor: BRAND, fontWeight: 700,
                '&:hover': { backgroundColor: '#4a3580' } }}>
              Import {validCount} {validCount === 1 ? 'job' : 'jobs'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobsListPage;
